import React, { useState, useContext, useEffect } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Flex,
  Text,
  Select,
  Box,
} from '@chakra-ui/react';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';
import { sendAddressSubmissionEmail, acceptCollabNotification } from '../../../services/flaskService';
import { collabApi } from 'services/api/collab';
import { useLocation, useNavigate } from 'react-router-dom';
import { Collab, Campaign } from '@foodfluence/shared';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { getInfluencerPrimaryHandle, isContactInfoValid } from 'utils/influencerUtils';
import { countryOptions } from '../../../types';
import { parsePhone } from '../../profile/utils';
import ShopifyProductSelection from './shopify/product-selection/ShopifyProductSelection';
import AddressFormFields, { AddressFormData, boardInputBorder, boardInputProps, boardLabelProps } from '../../../components/AddressFormFields';
import { useProductSelectionGuard } from '../utils/useProductSelectionGuard';
import PostByConfirm, { PostByConfirmState, usePostByConfirm } from './PostByConfirm';

interface ShippingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  collab: Collab;
  campaign: Campaign;
  /**
   * Go-live campaigns: the review page's confirmation. Its window is sent with the accept; a
   * GO_LIVE_* refusal shows the checkbox here with the new dates, keeping the address entered.
   * Without it (opened from the collab list or detail page) the modal keeps its own confirmation.
   */
  goLive?: PostByConfirmState;
}

const ShippingDetailsModal: React.FC<ShippingDetailsModalProps> = ({
  isOpen,
  onClose,
  collab,
  campaign,
  goLive: parentGoLive,
}) => {
  const ownGoLive = usePostByConfirm(campaign, collab);
  const goLive = parentGoLive ?? ownGoLive;
  const [addressData, setAddressData] = useState<AddressFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});
  const location = useLocation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [email, setEmail] = useState('');
  const clients = useContext(ClientDataContext);
  const client = clients[campaign?.clientID];
  const {influencerData} = useContext(InfluencerDataContext);
  const toast = useToast();
  const navigate = useNavigate();

  // Openable without going through the selection wizard — the collab list and detail page both
  // open it for a collab the brand accepted first. `acceptShipToHomeCollab` below would be
  // refused without a choice, so send the creator to make one; the wizard collects the address
  // itself and comes back to accept.
  const goToProductSelection = useProductSelectionGuard(collab, campaign);
  useEffect(() => {
    if (!isOpen) return;
    // The one place the guard runs on render rather than on a click: the modal has just opened
    // to collect an address for an accept that would be refused.
    if (goToProductSelection()) onClose();
  }, [isOpen, goToProductSelection, onClose]);


  // Initialize contact info from influencerData
  React.useEffect(() => {
    if (influencerData?.firstName && influencerData.firstName.toLowerCase() !== 'unknown') {
      setFirstName(influencerData.firstName);
    } else {
      setFirstName('');
    }
    if (influencerData?.lastName && influencerData.lastName.toLowerCase() !== 'unknown') {
      setLastName(influencerData.lastName);
    } else {
      setLastName('');
    }
    if (influencerData?.phoneNumber) {
      const parsed = parsePhone(influencerData.phoneNumber);
      setCountryCode(parsed.countryCode);
      setPhoneNumber(parsed.nationalNumber);
    } else {
      setCountryCode('+1');
      setPhoneNumber('');
    }
    if (influencerData?.email_address) {
      setEmail(influencerData.email_address);
    }
  }, [influencerData]);

  const isContactInfoValidForCampaign = 
    campaign.campaignType === "shopify"
      ? isContactInfoValid(firstName, lastName, email, phoneNumber, countryCode)
      : true;

  const handleAddressChange = (data: AddressFormData) => {
    setAddressData(data);
  };

  const handleVariantSelection = (productId: string, variantId: string) => {
    setSelectedVariants(prev => ({ ...prev, [productId]: variantId }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      if (!isContactInfoValidForCampaign) {
        toast({
          title: "Contact Information Required",
          description: campaign.campaignType === "shopify" 
            ? "Please enter your first name, last name, phone number, and email address"
            : "Please complete all required fields",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setIsSubmitting(false);
        return;
      }

      if (!addressData || !addressData.isAddressValidated) {
        toast({
          title: "Valid Address Required",
          description: "Please select an address from the dropdown",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setIsSubmitting(false);
        return;
      }

      const address2 = `${addressData.secondaryAddressType} ${addressData.secondaryAddressNumber}`;
      const isPendingAcceptance = collab?.status === "pendingInfluencerApproval";

      if (campaign.campaignType === "shopify") {
        // Only campaigns predating the selection catalog reach here: those with one are accepted
        // by the wizard, which collects the address and contact details itself.
        const variantIds = Object.values(selectedVariants).flat();
        const allProductsSelected = Object.keys(campaign.shopifyProducts).every(
          productId => selectedVariants[productId]?.length > 0
        );

        if (!allProductsSelected) {
          toast({
            title: "Selection Required",
            description: "Please select a variant for all products",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setIsSubmitting(false);
          return;
        }

        await collabApi.acceptShopifyCollab({
          collabId: collab.id,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          address1: addressData.streetAddress,
          address2: address2,
          city: addressData.city,
          state: addressData.state,
          zip: addressData.zip,
          country: addressData.country,
          phone: `${countryCode}${phoneNumber.trim()}`,
          email: email.trim(),
          variantIds,
          goLiveConfirmed: goLive.goLiveConfirmed,
        });
      } else {
        const fullAddress = address2 
          ? `${addressData.address.split(',')[0]}, ${address2},${addressData.address.substring(addressData.address.indexOf(',') + 1)}`
          : addressData.address;
        const requireDraftApproval = collab?.content && Object.keys(collab.content).length > 0 ? false : campaign.requireDraftApproval;
        await collabApi.acceptShipToHomeCollab({
          collabId: collab.id,
          shippingAddress: fullAddress,
          country: addressData.country,
          state: addressData.state,
          requireDraftApproval,
          goLiveConfirmed: goLive.goLiveConfirmed,
        });
        sendAddressSubmissionEmail(collab.id, client.email, getInfluencerPrimaryHandle(influencerData), campaign.clientID)
      }
      if (isPendingAcceptance) {
        acceptCollabNotification(collab.id);
      }
      onClose();

      toast({
        title: "Shipping details submitted",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate('/collabs');
    } catch (error) {
      console.error('Error submitting shipping details:', error);
      if (goLive.handleError(error)) return;
      toast({
        title: "Error submitting details",
        description: error instanceof Error ? error.message : 'Failed to submit shipping details',
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title="Shipping details"
      footer={
        // Sticky so Submit stays reachable on a long form; the dialog container is the scroller.
        <Flex
          position="sticky"
          bottom={0}
          gap={3}
          mt={5}
          mx={-5}
          px={5}
          pt="14px"
          pb="env(safe-area-inset-bottom)"
          bg="white"
          borderTop={`1px solid ${ff.line}`}
        >
          <Button variant="inkOutline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="lime"
            flex={1}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={
              !!goLive.blockedReason ||
              !isContactInfoValidForCampaign ||
              !addressData?.isAddressValidated || 
              (campaign.campaignType === "shopify" && 
                !Object.keys(campaign.shopifyProducts).every(
                  productId => selectedVariants[productId]?.length > 0
                )
              )
            }
          >
            Submit
          </Button>
        </Flex>
      }
    >
      <Box w="100%" maxW={{ base: '560px', lg: '720px' }} mx="auto">
        <Text fontSize="15px" lineHeight="1.4" color={ff.text2} mb={4}>
          {client?.accountName} will send the product to this address
      </Text>
        <VStack spacing="20px" align="stretch">
          {campaign.campaignType === "shopify" && (
            <>
              <Box>
                <Text as="h3" fontSize="13px" fontWeight="600" letterSpacing="0.08em" textTransform="uppercase" color="#66666A" mb="12px">
                  Contact Information
                </Text>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel {...boardLabelProps}>First Name</FormLabel>
                    <Input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Enter your first name"
                      {...boardInputProps}
                      {...boardInputBorder}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel {...boardLabelProps}>Last Name</FormLabel>
                    <Input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Enter your last name"
                      {...boardInputProps}
                      {...boardInputBorder}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel {...boardLabelProps}>Phone Number</FormLabel>
                    <Flex>
                      <Select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        width="30%"
                        mr={2}
                        {...boardInputProps}
                        {...boardInputBorder}
                      >
                        {countryOptions.map(option => (
                          <option key={option.code} value={option.code}>
                            {option.code}
                          </option>
                        ))}
                      </Select>
                      <Input
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                        placeholder="Phone Number"
                        type="tel"
                        flex={1}
                        {...boardInputProps}
                        {...boardInputBorder}
                      />
                    </Flex>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel {...boardLabelProps}>Email Address</FormLabel>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      type="email"
                      {...boardInputProps}
                      {...boardInputBorder}
                    />
                  </FormControl>
                </VStack>
              </Box>
              <Box h="1px" bg="#ECECEA" />
            </>
          )}
          <Box>
            <Text as="h3" fontSize="13px" fontWeight="600" letterSpacing="0.08em" textTransform="uppercase" color="#66666A" mb="12px">
              Shipping Address
            </Text>
            <VStack spacing={4}>
              <AddressFormFields
                onAddressChange={handleAddressChange}
              />
            </VStack>
          </Box>

          {campaign?.campaignType === "shopify" && (
            <>
              <Box h="1px" bg="#ECECEA" />
              <Box>
                <ShopifyProductSelection
                  clientId={campaign.clientID}
                  availableVariants={campaign.shopifyProducts}
                  selectedVariants={selectedVariants}
                  onVariantSelection={handleVariantSelection}
                />
              </Box>
            </>
          )}
          {/* After a go-live refusal: re-confirm the new dates here without losing the address. */}
          {(goLive.changed || goLive.blockedReason) && <PostByConfirm state={goLive} />}
        </VStack>
      </Box>
    </ModalShell>
  );
};

export default ShippingDetailsModal; 